import type { WizardFieldErrors } from "@/base";
import type { FieldOption, ProductManifest } from "@/lib/brokerProducts";
import { NO_COVERAGE_REASON } from "@/lib/submissions/wc/history";
import {
  COVERAGE,
  isAsked,
  sectionFields,
  shownAnswers,
  validateAnswers,
  type WcAnswers,
} from "@/lib/submissions/wc/questions";

// The WC wizard's Questions and coverage › Coverage (BRK-S392), as pure rules
// (PMS-S811; Nav and Tyler, 10/20–22):
// - The employers' liability limits are one choice of the sets the manifest's
//   `elLimitCombinations` publishes, which fills the three limit fields. They
//   stay three plain numbers on the wire, which PMS sends Great American. A
//   PMS before PMS-S811 publishes no sets, and the three are asked as before.
// - The sets are the three Signal offers, and the broker sees the set, never
//   its three amounts (BRK-S508, PMS-S922; Tyler, 10/29).
// - ACORD 130's declined, cancelled or non-renewed question is asked here,
//   with its reason after a Yes.
// - A No to it, when Characteristics & History says coverage lapsed, is
//   flagged, never blocked.

/** The three limits, in the order a combination lists them. */
export const EL_LIMIT_KEYS = [
  "elEachAccident",
  "elEachEmployee",
  "elEachPolicy",
] as const;
type ElLimitKey = (typeof EL_LIMIT_KEYS)[number];

/** What each column of a combination is, as its options read. */
export const EL_LIMITS_HEADER =
  "Each accident | Disease each employee | Disease policy limit";
export const EL_LIMITS_LABEL = "Employers' liability limits";
const EL_LIMITS_REQUIRED = "Choose the employers' liability limits.";

/** ACORD 130's declined, cancelled or non-renewed question (PMS-S811). */
export const DECLINED = "wgiPriorCoverageDeclined";
/** History's reason for no coverage that a declined No contradicts. */
const LAPSED = "coverage_lapsed";
export const CONTRADICTION =
  "You said coverage lapsed or was cancelled in the past 4 years. Check this answer.";

/** Coverage's own words for a question left unanswered, by key. */
const COVERAGE_COPY: Readonly<Record<string, string>> = {
  priorCoverageDeclinedReason: "Choose why it was declined.",
};

const usd = (n: number) => `$${n.toLocaleString("en-US")}`;
/** A set's three amounts in full, as its option reads. */
const setText = (amounts: readonly number[]) => amounts.map(usd).join(" | ");

/** Whether the limits are one choice: the manifest publishes the
 *  combinations, and Coverage asks all three limits. */
export function elLimitsAsked(manifest: ProductManifest | null): boolean {
  const keys = new Set(sectionFields(manifest, COVERAGE).map((f) => f.key));
  return (
    (manifest?.elLimitCombinations?.length ?? 0) > 0 &&
    EL_LIMIT_KEYS.every((k) => keys.has(k))
  );
}

/** Each combination as an option: its three amounts, "$100,000 | $100,000 |
 *  $500,000", in the order PMS lists them. */
export function elLimitOptions(
  combinations: readonly (readonly number[])[],
): FieldOption[] {
  return combinations.map((c) => ({ value: c.join("|"), label: setText(c) }));
}

/** Whether a field is the second or third limit, which the first limit's
 *  place stands for while the limits are one choice. */
export function isOtherLimit(key: string): boolean {
  return EL_LIMIT_KEYS.slice(1).some((k) => k === key);
}

/** The chosen combination, "" until all three limits hold a number. */
export function elLimitValue(answers: WcAnswers): string {
  const amounts = EL_LIMIT_KEYS.map((k) => answers[k]);
  return amounts.every((a) => typeof a === "number") ? amounts.join("|") : "";
}

/** The chosen set as one line, as its option reads; undefined until all three
 *  limits hold a number. A set PMS holds that isn't offered reads as it
 *  stands. */
export function elLimitText(answers: WcAnswers): string | undefined {
  const amounts = EL_LIMIT_KEYS.map((k) => answers[k]);
  return amounts.every((a): a is number => typeof a === "number")
    ? setText(amounts)
    : undefined;
}

/** The answers as Coverage asks them: with the limits one choice, the first
 *  limit stands for the set, so a count takes it once. */
export function asOneLimit(
  manifest: ProductManifest | null,
  answers: WcAnswers,
): WcAnswers {
  if (!elLimitsAsked(manifest)) return answers;
  return Object.fromEntries(
    Object.entries(answers).filter(([key]) => !isOtherLimit(key)),
  );
}

/** The three limits a combination fills, as plain numbers; "" clears them. */
export function elLimitPatch(
  value: string,
): Record<ElLimitKey, number | undefined> {
  const amounts = value ? value.split("|").map(Number) : [];
  return {
    elEachAccident: amounts[0],
    elEachEmployee: amounts[1],
    elEachPolicy: amounts[2],
  };
}

/** Coverage's checks: its questions in its own words, and the limits as one
 *  combination the line writes, keyed on the first limit, where PMS's
 *  refusal of them lands too. */
export function validateCoverage(
  manifest: ProductManifest | null,
  answers: WcAnswers,
): WizardFieldErrors {
  const errors: Record<string, string> = {
    ...validateAnswers(manifest, answers, COVERAGE, COVERAGE_COPY),
  };
  if (!elLimitsAsked(manifest)) return errors;
  for (const key of EL_LIMIT_KEYS) delete errors[key];
  const offered = elLimitOptions(manifest?.elLimitCombinations ?? []);
  if (!offered.some((o) => o.value === elLimitValue(answers))) {
    errors.elEachAccident = EL_LIMITS_REQUIRED;
  }
  return errors;
}

/** Whether a No to the declined question contradicts Characteristics &
 *  History's answer that coverage lapsed or was cancelled. */
export function declinedContradicts(
  manifest: ProductManifest | null,
  answers: WcAnswers,
): boolean {
  const reason = manifest?.fields.find((f) => f.key === NO_COVERAGE_REASON);
  const shown = shownAnswers(manifest?.fields ?? [], answers);
  return (
    reason !== undefined &&
    isAsked(reason, shown) &&
    shown[NO_COVERAGE_REASON] === LAPSED &&
    shown[DECLINED] === false
  );
}
